/**
 * Service Connections Service
 * Handles OAuth connections for Gmail, Drive, Calendar
 * Similar pattern to authService.js
 */

class ServiceConnectionsService {
  constructor() {
    this.backendURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    this.pollInterval = null;
    this.connections = [];
  }

  /**
   * Get authentication token
   */
  getToken() {
    return localStorage.getItem('sarvik_token');
  }

  /**
   * Fetch all connections for current user
   */
  async fetchConnections() {
    try {
      const token = this.getToken();
      if (!token) {
        console.error('No auth token found');
        return [];
      }

      const response = await fetch(`${this.backendURL}/api/oauth/connections`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch connections');
      }

      const data = await response.json();
      this.connections = data.connections || [];
      return this.connections;
    } catch (error) {
      console.error('Error fetching connections:', error);
      return [];
    }
  }

  /**
   * Initiate connection for a service (Gmail, Drive, Calendar)
   */
  async initiateConnection(service) {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Check if already connected
      const existing = this.connections.find(
        conn => conn.service === service && conn.connected
      );
      
      if (existing) {
        console.log(`${service} already connected as ${existing.service_email}`);
        return { 
          already_connected: true, 
          service_email: existing.service_email 
        };
      }

      // Call backend to get OAuth URL
      const response = await fetch(
        `${this.backendURL}/api/oauth/connect/${service}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to initiate connection');
      }

      const data = await response.json();

      // If already connected, return early
      if (data.already_connected) {
        return data;
      }

      // Open OAuth URL in browser (Electron)
      const isElectron = window.electronAPI !== undefined;
      
      if (isElectron && window.electronAPI.openOAuthURL) {
        window.electronAPI.openOAuthURL(data.authorization_url);
      } else {
        window.open(data.authorization_url, '_blank');
      }

      // Start polling for connection completion
      return this.startPolling(service);

    } catch (error) {
      console.error(`Failed to initiate ${service} connection:`, error);
      throw error;
    }
  }

  /**
   * Poll backend to check if connection is complete
   * Similar to SARVIK login polling
   */
  async startPolling(service) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 120; // 2 minutes total
      
      // Polling with exponential backoff
      let currentInterval = 1000; // Start with 1 second
      const maxInterval = 5000; // Max 5 seconds
      const backoffFactor = 1.1;
      
      // Clear any existing interval
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
      }

      const poll = async () => {
        attempts++;

        try {
          // Fetch connections to see if new one appeared
          const connections = await this.fetchConnections();
          const newConnection = connections.find(
            conn => conn.service === service && conn.connected
          );

          if (newConnection) {
            // Success! Connection established
            clearInterval(this.pollInterval);
            console.log(`✅ ${service} connected as ${newConnection.service_email}`);
            resolve({
              success: true,
              service: service,
              service_email: newConnection.service_email,
              connection: newConnection
            });
            return;
          }

          // Check if max attempts reached
          if (attempts >= maxAttempts) {
            clearInterval(this.pollInterval);
            reject(new Error('Connection timeout - user did not complete OAuth'));
            return;
          }

          // Exponential backoff
          if (attempts > 5 && currentInterval < maxInterval) {
            clearInterval(this.pollInterval);
            currentInterval = Math.min(
              currentInterval * backoffFactor,
              maxInterval
            );
            this.pollInterval = setInterval(poll, currentInterval);
          }

        } catch (error) {
          console.error('Polling error:', error);
          // Continue polling on errors (might be temporary)
        }
      };

      // Start polling
      this.pollInterval = setInterval(poll, currentInterval);
      console.log(`🔄 Polling for ${service} connection...`);
    });
  }

  /**
   * Disconnect a service
   */
  async disconnect(service) {
    try {
      const token = this.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(
        `${this.backendURL}/api/oauth/disconnect/${service}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to disconnect');
      }

      const data = await response.json();
      
      // Update local cache
      this.connections = this.connections.filter(
        conn => conn.service !== service
      );

      console.log(`✅ ${service} disconnected`);
      return data;

    } catch (error) {
      console.error(`Failed to disconnect ${service}:`, error);
      throw error;
    }
  }

  /**
   * Stop polling (cleanup)
   */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  /**
   * Check if a service is connected
   */
  isConnected(service) {
    return this.connections.some(
      conn => conn.service === service && conn.connected
    );
  }

  /**
   * Get connection details for a service
   */
  getConnection(service) {
    return this.connections.find(
      conn => conn.service === service
    );
  }
}

// Export singleton instance
export default new ServiceConnectionsService();
